import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '@services/api-client/api-client.service';
import { SerialService } from './serial.service';
import { DeviceDateTimeInfo, DeviceInfoService } from './device-info.service';
import { VerifyFirmwareUpgradeResponse } from '@services/api-client/responses/verify-firmware-upgrade-response.type';
import { DeviceClockSyncOptions, DeviceClockSyncService } from './device-clock-sync.service';

export type DeviceInfo = DeviceDateTimeInfo & {
    softwareVersion: string;
    versionText: string;
    serialNumber: string;
};

export type VerificationResult = {
    success: boolean;
    deviceInfo: DeviceInfo | null;
    verificationResponse: VerifyFirmwareUpgradeResponse | null;
};

@Injectable({
    providedIn: 'root'
})
export class DeviceVerificationService {
    public constructor(
        private apiClient: ApiClientService,
        private deviceInfoService: DeviceInfoService,
        private deviceClockSyncService: DeviceClockSyncService
    ) {}

    /**
     * Complete device verification flow: retrieves device info and verifies with server.
     * This is the main method for most use cases.
     *
     * @param serialService - Serial service for device communication
     * @param loginRequired - Whether device login is required
     * @param password - Device password (if login required)
     * @param upgradedVersionId - Optional version ID from upgrade (null for auto-verification)
     * @returns VerificationResult with device info and verification response
     */
    public async verifyDevice(
        serialService: SerialService,
        loginRequired: boolean = false,
        password: string | null = null,
        resolveLoginRequirements: boolean = false,
        clockSyncOptions: DeviceClockSyncOptions = {}
    ): Promise<VerificationResult> {
        const connectionSession = serialService.connectionSessionId;
        const result: VerificationResult = {
            success: false,
            deviceInfo: null,
            verificationResponse: null
        };

        try {
            console.log('[DeviceVerification] Starting complete device verification flow...');

            // Step 1: Retrieve device version
            console.log('[DeviceVerification] Retrieving device version...');
            let version: string;
            try {
                version = await this.deviceInfoService.getDeviceVersion(serialService);
                if (!version?.trim()) {
                    throw new Error('Device returned no firmware version information');
                }
                console.log(`[DeviceVerification] ✓ Device version retrieved`);
            } catch (error: unknown) {
                const errorMsg: string = error instanceof Error ? error.message : JSON.stringify(error);
                console.error(`[DeviceVerification] ✗ Failed to retrieve device version: ${errorMsg}`);
                return result; // Return early if version retrieval fails
            }

            const softwareVersion = this.extractSoftwareVersion(version);
            console.log(`[DeviceVerification] ✓ Software version extracted: ${softwareVersion}`);
            if (resolveLoginRequirements) {
                const firmwareInfo = await firstValueFrom(
                    this.apiClient.checkFirmwareVersion$({ currentVersion: softwareVersion })
                );
                loginRequired = firmwareInfo.loginRequired;
                password = firmwareInfo.password;
            }

            // Step 2: Retrieve serial number
            console.log('[DeviceVerification] Retrieving device serial number...');
            let serialNumber: string;
            try {
                serialNumber = await this.deviceInfoService.getDeviceSerialNumber(
                    serialService,
                    loginRequired,
                    password
                );
                console.log(`[DeviceVerification] ✓ Device serial number retrieved`);
            } catch (error: unknown) {
                const errorMsg: string = error instanceof Error ? error.message : JSON.stringify(error);
                console.error(`[DeviceVerification] ✗ Failed to retrieve device serial number: ${errorMsg}`);
                return result; // Return early if serial number retrieval fails
            }

            // Store device info in result
            const deviceDateTimeInfo = await this.deviceInfoService.getDeviceDateTime(serialService, softwareVersion);
            result.deviceInfo = {
                softwareVersion,
                versionText: version,
                serialNumber,
                ...deviceDateTimeInfo
            };

            // Step 3: Verify with server
            console.log('[DeviceVerification] Sending verification request to server...');
            const verificationResponse = await this.verifyDeviceInfo(
                result.deviceInfo,
                serialService,
                connectionSession,
                clockSyncOptions
            );

            if (verificationResponse) {
                result.verificationResponse = verificationResponse;
                if (verificationResponse.isVerified) {
                    result.success = true;
                    console.log('[DeviceVerification] ✓ Device verification successful');
                } else {
                    console.warn(
                        '[DeviceVerification] ✗ Device verification failed on server:',
                        verificationResponse.message
                    );
                }
            } else {
                console.error('[DeviceVerification] ✗ Verification request failed (no response)');
            }

            return result;
        } catch (error: unknown) {
            const errorMsg: string = error instanceof Error ? error.message : JSON.stringify(error);
            console.error(`[DeviceVerification] ✗ Unexpected error in verification flow: ${errorMsg}`);
            return result;
        }
    }

    /**
     * Verifies device information with the server.
     * This method only calls the API endpoint - it does not retrieve device info.
     * All errors are logged to console and do not throw exceptions.
     *
     * @param deviceInfo - Device information (softwareVersion, versionText, serialNumber)
     * @param upgradedVersionId - Optional version ID from the upgrade (null for auto-verification)
     * @returns Promise<VerifyFirmwareUpgradeResponse | null> - Verification result or null if error
     */
    public async verifyDeviceInfo(
        deviceInfo: DeviceInfo,
        serialService: SerialService,
        connectionSession = serialService.connectionSessionId,
        clockSyncOptions: DeviceClockSyncOptions = {}
    ): Promise<VerifyFirmwareUpgradeResponse | null> {
        try {
            if (!serialService.isConnected() || connectionSession !== serialService.connectionSessionId) return null;
            console.log('[DeviceVerification] Calling verification API...');

            const pendingCalibration = this.deviceClockSyncService.getPendingAdjustment(deviceInfo.serialNumber);
            const request = {
                softwareVersion: deviceInfo.softwareVersion,
                versionText: deviceInfo.versionText,
                serialNumber: deviceInfo.serialNumber,
                deviceDateTime: deviceInfo.deviceDateTime,
                calibration: pendingCalibration ? { ...pendingCalibration, clockSynchronized: false } : undefined
            };

            const result = await firstValueFrom(this.apiClient.verifyFirmwareUpgrade$(request));

            if (result.isVerified) {
                console.log('[DeviceVerification] ✓ API verification successful');
            } else {
                console.warn('[DeviceVerification] ✗ API verification failed:', result.message);
            }

            if (serialService.isConnected() && connectionSession === serialService.connectionSessionId) {
                try {
                    await this.deviceClockSyncService.synchronize(serialService, deviceInfo, result, clockSyncOptions);
                } catch (error: unknown) {
                    console.warn('[DeviceVerification] Clock synchronization did not complete:', error);
                }
            }
            return result;
        } catch (error: unknown) {
            const errorMsg: string = error instanceof Error ? error.message : JSON.stringify(error);
            console.error(`[DeviceVerification] ✗ Failed to call verification API: ${errorMsg}`);
            return null;
        }
    }

    /**
     * Extracts the software version from the device version string.
     */
    private extractSoftwareVersion(versionString: string): string {
        if (!versionString) return 'unknown';

        // First try to find "Software Version: X.XXX"
        const match = versionString.match(/Software Version:\s*([^\s\n]+)/);
        if (match) {
            return match[1].trim();
        }

        // Fallback: Look for "Main Version:" section
        const mainSectionMatch = versionString.match(/Main Version:([\s\S]*?)(?=Core Version:|$)/);
        if (mainSectionMatch) {
            const mainSection = mainSectionMatch[1];
            const versionMatch = mainSection.match(/^\s*Version:\s*([^\s\n]+)/m);
            if (versionMatch) {
                return versionMatch[1].trim();
            }
        }

        return 'unknown';
    }
}
